import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, MenuItem, Select, Snackbar,
  Stack, TextField, Typography
} from '@mui/material';
import { CheckCircle, Block, Refresh } from '@mui/icons-material';
import AdminHeader from '../../components/admin/adminHeader.tsx';
import Slidemenu from '../../components/admin/adminSlidemenu.tsx';

const API = process.env.REACT_APP_SERVER_ENDPOINT || '';
const AUTH_KEY = '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3';

type Product = {
  product_id: number; product_name: string; product_main_image?: string;
  product_price?: number; product_retail_price?: number; product_stock?: number;
  approval_status: 'pending'|'approved'|'rejected'|'blocked'; approval_notes?: string;
  vendor_id?: number; vendor_name?: string; business_name?: string;
  vendor_email?: string; vendor_contact?: string;
};

const request = async (url: string, body?: any) => {
  const token = localStorage.getItem('admin_token');
  const response = await fetch(`${API}${url}`, {
    method: body ? 'POST' : 'GET',
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
      authkey: AUTH_KEY,
      ...(body ? {'Content-Type': 'application/json'} : {})
    },
    body: body ? JSON.stringify(body) : undefined
  });
  const data = await response.json();
  if (!response.ok || data.error) throw new Error(data.message || 'Request failed');
  return data;
};

export default function VendorProductApprovals() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [status, setStatus] = useState(() => localStorage.getItem('tab_vendor_product_approval') || 'pending');
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState<{open:boolean; action:'approved'|'rejected'|'blocked'; product?:Product}>({open:false,action:'approved'});
  const [notes, setNotes] = useState('');
  const [snack, setSnack] = useState({open:false,message:'',severity:'success' as 'success'|'error'});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await request(`/admin/vendor-products?status=${encodeURIComponent(status)}`);
      setProducts(data.data || []);
    } catch (e:any) {
      setSnack({open:true,message:e.message || 'Unable to load vendor products',severity:'error'});
    } finally { setLoading(false); }
  }, [status]);

  useEffect(() => { localStorage.setItem('tab_vendor_product_approval', status); load(); }, [status, load]);

  const decide = async () => {
    if (!dialog.product) return;
    try {
      await request(`/admin/vendor-products/${dialog.action === 'approved' ? 'approve' : dialog.action === 'rejected' ? 'reject' : 'block'}`, {
        product_id: dialog.product.product_id, notes
      });
      setSnack({
        open:true,
        message: dialog.action === 'approved' ? 'Product approved and published.' : dialog.action === 'rejected' ? 'Product rejected.' : 'Product blocked and unpublished.',
        severity:'success'
      });
      setDialog({open:false,action:'approved'}); setNotes(''); load();
    } catch (e:any) {
      setSnack({open:true,message:e.message || 'Unable to update product',severity:'error'});
    }
  };

  return (
    <Box sx={{display:'flex',minHeight:'100vh',bgcolor:'#f5f6f7'}}>
      <Slidemenu onToggle={() => setDrawerOpen(!drawerOpen)} mobileOpen={drawerOpen} />
      <Box sx={{flex:1,minWidth:0}}>
        <AdminHeader onToggleMenu={() => setDrawerOpen(!drawerOpen)} />
        <Box sx={{p:{xs:2,md:3},maxWidth:1500,mx:'auto'}}>
          <Box sx={{display:'flex',justifyContent:'space-between',alignItems:'center',mb:2,flexWrap:'wrap',gap:1}}>
            <Box>
              <Typography variant="h5" fontWeight={700}>Vendor Product Approval</Typography>
              <Typography variant="body2" color="text.secondary">
                Vendor products stay hidden until an admin approves them. Approved products can be blocked later.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Select size="small" value={status} onChange={e=>setStatus(e.target.value)} sx={{minWidth:130}}>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem><MenuItem value="blocked">Blocked</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </Select>
              <Button variant="outlined" startIcon={<Refresh/>} onClick={load}>Refresh</Button>
            </Stack>
          </Box>

          <Card sx={{borderRadius:2}}>
            <CardContent>
              {loading ? <Box sx={{py:8,textAlign:'center'}}><CircularProgress/></Box> :
               products.length === 0 ? <Alert severity="info">No {status} vendor products.</Alert> :
               <Stack spacing={1.5}>
                {products.map(p => (
                  <Box key={p.product_id} sx={{p:2,border:'1px solid #e3e5e8',borderRadius:2,display:'flex',gap:2,alignItems:'center',flexWrap:'wrap'}}>
                    <Box sx={{width:72,height:72,borderRadius:1,bgcolor:'#f0f1f2',overflow:'hidden',flexShrink:0}}>
                      {p.product_main_image && <img src={`${API}/images/${p.product_main_image}`} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}} />}
                    </Box>
                    <Box sx={{flex:1,minWidth:220}}>
                      <Typography fontWeight={700}>{p.product_name}</Typography>
                      <Typography variant="body2" color="text.secondary">{p.business_name || p.vendor_name || `Vendor #${p.vendor_id}`}</Typography>
                      <Typography variant="caption" color="text.secondary">{p.vendor_email || ''} {p.vendor_contact ? `• ${p.vendor_contact}` : ''}</Typography>
                    </Box>
                    <Box sx={{minWidth:140}}>
                      <Typography variant="body2">Stock: {Number(p.product_stock || 0)}</Typography>
                      <Typography variant="body2">Price: AED {Number(p.product_retail_price || p.product_price || 0).toFixed(2)}</Typography>
                      <Chip size="small" label={p.approval_status} color={p.approval_status==='approved'?'success':p.approval_status==='blocked'?'error':'warning'} />
                    </Box>
                    <Stack direction="row" spacing={1}>
                      {p.approval_status !== 'approved' &&
                        <Button size="small" variant="contained" color="success" startIcon={<CheckCircle/>}
                          onClick={()=>{setNotes('');setDialog({open:true,action:'approved',product:p})}}>Approve</Button>}
                      {p.approval_status !== 'approved' && p.approval_status !== 'rejected' &&
                        <Button size="small" variant="outlined" color="error" onClick={()=>{setNotes('');setDialog({open:true,action:'rejected',product:p})}}>Reject</Button>}
                      {p.approval_status !== 'blocked' &&
                        <Button size="small" variant="outlined" color="error" startIcon={<Block/>}
                          onClick={()=>{setNotes('');setDialog({open:true,action:'blocked',product:p})}}>Block</Button>}
                    </Stack>
                  </Box>
                ))}
               </Stack>}
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Dialog open={dialog.open} onClose={()=>setDialog({open:false,action:'approved'})} maxWidth="sm" fullWidth>
        <DialogTitle>{dialog.action==='approved' ? 'Approve Product' : dialog.action==='rejected' ? 'Reject Product' : 'Block Product'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{mb:2}}>
            {dialog.product?.product_name} — {dialog.action==='approved' ? 'this will publish the product.' : 'this will hide the product until corrected.'}
          </Typography>
          <TextField fullWidth multiline minRows={3} label={dialog.action==='rejected' ? 'Rejection reason *' : 'Admin note (optional)'} value={notes} onChange={e=>setNotes(e.target.value)} error={dialog.action==='rejected' && !notes.trim()} helperText={dialog.action==='rejected' && !notes.trim() ? 'Rejection reason is mandatory.' : ''} />
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setDialog({open:false,action:'approved'})}>Cancel</Button>
          <Button variant="contained" color={dialog.action==='approved'?'success':'error'} onClick={decide} disabled={dialog.action==='rejected' && !notes.trim()}>
            {dialog.action==='approved'?'Approve & Publish':dialog.action==='rejected'?'Reject Product':'Block & Unpublish'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={()=>setSnack({...snack,open:false})}
        message={snack.message} />
    </Box>
  );
}
